import { transformBirthdayDocument } from 'shell/utils/consult-person/request-transformer/registry-office/birthday-document';
import { transformGenerality } from 'shell/utils/consult-person/request-transformer/registry-office/generality';
import { transformSubjectAttributes } from 'shell/utils/consult-person/request-transformer/registry-office/subject-attributes';
import { transformCitizenship } from 'shell/utils/consult-person/request-transformer/registry-office/citizenship';
import { NewPersonVM } from 'shell/components/shared/sipo-person-form/new-person';
import { transformCivilStatus } from 'shell/utils/consult-person/request-transformer/marital-status/civil-status';
import { transformMarriage } from 'shell/utils/consult-person/request-transformer/marital-status/marriage';
import { CivilStatusID } from 'shell/constants/civil-status';
import { transformWidowhood } from 'shell/utils/consult-person/request-transformer/marital-status/widowhood';
import { transformVoidMarriage } from 'shell/utils/consult-person/request-transformer/marital-status/void-marriage';
import { transformCivilUnion } from 'shell/utils/consult-person/request-transformer/marital-status/civil-union';
import { transformVoidCivilUnion } from 'shell/utils/consult-person/request-transformer/marital-status/void-civil-union';
import { transformProfessionTitle } from 'shell/utils/consult-person/request-transformer/other-datas/profession-title';
import { transformDrivingLicenseBooklet } from 'shell/utils/consult-person/request-transformer/other-datas/driving-license-booklet';
import { transformIdentityCard } from 'shell/utils/consult-person/request-transformer/documents/identity-card';
import { transformResidencyPermit } from 'shell/utils/consult-person/request-transformer/documents/residency-permit';
import { RequestNewPerson, RequestNewPersonEdit } from 'shell/models/request-new-person';
import { ResidenceOrigin } from 'shell/view-models/personal-data';

export const transformNewPersonEdit = (values: NewPersonVM): RequestNewPersonEdit => {
  const codiceCittadinanza = (values.citizenship || [])[0]?.code || undefined;

  const codiceStatoCivile = values.civilStatus?.status || undefined;

  const cognomePadre = values.paternity?.generality?.surname || undefined;
  const nomePadre = values.paternity?.generality?.name || undefined;

  const cognomeMadre = values.maternity?.generality?.surname || undefined;
  const nomeMadre = values.maternity?.generality?.name || undefined;

  return {
    codiceCittadinanza,
    codiceStatoCivile,
    cognomePadre,
    nomePadre,
    cognomeMadre,
    nomeMadre,
  };
};

const transformResidenceOrigin = (values?: ResidenceOrigin): RequestNewPerson | undefined => {
  if (!values) {
    return undefined;
  }

  return {
    irreperibile: values?.irreperibile || false,
    minorePreadozione: values?.neverRegistered || false,
    comune: values?.isForeign
      ? undefined
      : values?.origin?.description || values?.origin?.id || values?.origin?.province
      ? {
          nomeComune: values?.origin?.description,
          codiceIstat: values?.origin?.id,
          siglaProvinciaIstat: values?.origin?.province,
        }
      : undefined,
    localita:
      values?.isForeign &&
      (values?.originState?.value ||
        values?.originState?.id ||
        values?.originMunicipalityForeign?.description)
        ? {
            descrizioneStato: values?.originState?.value,
            codiceStato: values?.originState?.id,
            provinciaContea: values?.originState?.province,
            descrizioneLocalita: values?.originMunicipalityForeign?.description,
          }
        : undefined,
    comunePrecedente:
      values?.previousOriginCode || values?.previousOriginSipoCode
        ? {
            codice: values?.previousOriginCode,
            codiceSipo: values?.previousOriginSipoCode,
          }
        : undefined,
  };
};

export const transformNewPerson = (values: NewPersonVM): RequestNewPerson => {
  // step registry-office
  const generalita =
    transformGenerality(
      values.generality
        ? {
            ...values.generality,
            foreign: values.generality?.foreign
              ? {
                  ...values.generality?.foreign,
                  state: values.generality?.foreign?.state
                    ? {
                        ...values.generality?.foreign?.state,
                        province:
                          values.generality?.foreign?.municipality?.el?.cdTipoComune === '3'
                            ? values.generality?.foreign?.municipality?.el?.cdNazione
                            : values.generality?.foreign?.municipality?.province,
                      }
                    : undefined,
                }
              : undefined,
          }
        : {},
      {
        fiscalCode: values?.generality?.fiscalCode,
        name: values?.generality?.name,
        surname: values?.generality?.surname,
        withoutSurname: values?.generality?.withoutSurname,
        withoutName: values?.generality?.withoutName,
      },
      {}
    ) || undefined;

  // step registry-office
  const attributiSoggetto = transformSubjectAttributes(values.subjectAttributes) || undefined;

  // step registry-office
  const attoNascita = transformBirthdayDocument(values.birthdayDocument) || undefined;

  // step registry-office
  const cittadinanza = transformCitizenship(values.citizenship) || undefined;

  // step registry-office
  const paternita = values.paternity?.generality
    ? {
        generalita:
          transformGenerality(
            values.paternity?.generality || {},
            {
              fiscalCode: values?.paternity?.generality?.fiscalCode,
              name: values?.paternity?.generality?.name,
              surname: values?.paternity?.generality?.surname,
              withoutSurname: values?.paternity?.generality?.withoutSurname,
              withoutName: values?.paternity?.generality?.withoutName,
            },
            {}
          ) || undefined,
      }
    : undefined;

  // step registry-office
  const maternita = values.maternity?.generality
    ? {
        generalita:
          transformGenerality(
            values.maternity?.generality || {},
            {
              fiscalCode: values?.maternity?.generality?.fiscalCode,
              name: values?.maternity?.generality?.name,
              surname: values?.maternity?.generality?.surname,
              withoutSurname: values?.maternity?.generality?.withoutSurname,
              withoutName: values?.maternity?.generality?.withoutName,
            },
            {}
          ) || undefined,
      }
    : undefined;

  // step civil status
  const statoCivile = transformCivilStatus(values.civilStatus) || undefined;

  // code civil status
  const status = values.civilStatus?.status?.toString();

  // step civil status
  const matrimonio =
    status === CivilStatusID.coniugato ||
    status === CivilStatusID.vedovo ||
    status === CivilStatusID.divorziato
      ? transformMarriage(
          values.marriage
            ? {
                ...values.marriage,
                person: {
                  fiscalCode: values.marriage?.generality?.fiscalCode,
                  name: values.marriage?.generality?.name,
                  surname: values.marriage?.generality?.surname,
                  withoutSurname: values.marriage?.generality?.withoutSurname,
                  withoutName: values.marriage?.generality?.withoutName,
                },
              }
            : undefined
        ) || undefined
      : undefined;

  // step civil status
  const vedovanza =
    status === CivilStatusID.vedovo ? transformWidowhood(values.widowhood) || undefined : undefined;

  // step civil status
  const annullamentoMatrimonio =
    status === CivilStatusID.divorziato
      ? transformVoidMarriage(values.voidMarriage) || undefined
      : undefined;

  // step civil status
  const unioneCivile =
    status === CivilStatusID.unitoCivilmente || status === CivilStatusID.scioglimentoUnitoCivilmente
      ? transformCivilUnion(
          values.civilUnion
            ? {
                ...values.civilUnion,
                person: {
                  fiscalCode: values.civilUnion?.generality?.fiscalCode,
                  name: values.civilUnion?.generality?.name,
                  surname: values.civilUnion?.generality?.surname,
                  withoutSurname: values.civilUnion?.generality?.withoutSurname,
                  withoutName: values.civilUnion?.generality?.withoutName,
                },
              }
            : undefined
        ) || undefined
      : undefined;

  // step civil status
  const scioglimentoUnione =
    status === CivilStatusID.scioglimentoUnitoCivilmente
      ? transformVoidCivilUnion(values.voidCivilUnion) || undefined
      : undefined;

  // step other datas
  const altriDati = transformProfessionTitle(values.professionTitle) || undefined;

  // setp document
  const autoveicoli = transformDrivingLicenseBooklet(values.drivingLicenseBooklet) || undefined;

  // setp document
  const cartaIdentita = transformIdentityCard(values.identityCard) || undefined;

  // setp document
  const permessoSoggiorno = transformResidencyPermit(values.residencyPermit) || undefined;

  // step provenienza
  const provenienza = transformResidenceOrigin(values.residenceOrigin) || undefined;

  return {
    generalita,
    attributiSoggetto,
    attoNascita,
    cittadinanza,
    paternita,
    maternita,
    statoCivile,
    matrimonio,
    vedovanza,
    annullamentoMatrimonio,
    unioneCivile: unioneCivile
      ? {
          ...unioneCivile,
          scioglimentoUnione: scioglimentoUnione
            ? {
                ...scioglimentoUnione,
              }
            : undefined,
        }
      : undefined,
    altriDati,
    autoveicoli,
    cartaIdentita,
    permessoSoggiorno,
    ...(provenienza || {}),
  };
};
